import { useEffect, useRef, useCallback, useState } from "react";
import type {
  EntityChangeEvent,
  InteractiveEvent,
  StreamEvent,
  WsClientMessage,
  WsEvent,
  WsServerMessage,
} from "@yagni/shared";

interface GatewayInfo {
  gatewayUrl: string;
  gatewayToken: string;
  mode?: string;
}

interface GatewayConnectResponse {
  type: string;
  id?: string;
  error?: unknown;
}

const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 1_000;

function reconnectDelay(attempt: number): number {
  const delay = Math.min(
    BASE_RECONNECT_DELAY * 2 ** attempt,
    MAX_RECONNECT_DELAY
  );
  const jitter = Math.random() * BASE_RECONNECT_DELAY;
  return Math.min(delay + jitter, MAX_RECONNECT_DELAY);
}

/**
 * Tell data hooks that a socket came back after a drop (never the first
 * connect). Broadcasts sent while it was down are lost, so listeners such as
 * useWorkFeed and useWorkItemActivity refetch on this event.
 */
function dispatchReconnected(): void {
  window.dispatchEvent(new CustomEvent("ws.reconnected"));
}

async function fetchGatewayInfo(): Promise<GatewayInfo | null> {
  try {
    const res = await fetch("/api/workspace/gateway-info", {
      credentials: "include",
    });
    if (!res.ok) return null;
    return (await res.json()) as GatewayInfo;
  } catch {
    return null;
  }
}

function buildGatewayWsUrl(gatewayUrl: string): string | null {
  // Defensive: a malformed gatewayUrl must not throw here — this runs inside a
  // floating `fetchGatewayInfo().then(...)` (no catch), so a throw would become
  // an unhandled rejection and crash the connect path. Return null → the caller
  // treats it like missing info and retries with backoff.
  try {
    const url = new URL(gatewayUrl);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.toString();
  } catch {
    return null;
  }
}

function isWsServerMessageType(type: string): type is WsServerMessage["type"] {
  return [
    "message",
    "error",
    "subscribed",
    "subscribed_conversation",
    "proposal.created",
    "typing.start",
    "typing.stop",
    "stream.delta",
    "stream.complete",
    "tool.called",
    "tool.result",
    "approval.requested",
    "approval.resolved",
    "guardrail.triggered",
    "calendar.brief.updated",
    "page.created",
    "page.updated",
    "builder.activity",
    "builder.status",
    "run.activity",
    "run.status",
    "work_item.updated",
    "work_item_activity.created",
    "work_item_comment.created",
    "work_item_comment.deleted",
    "work_item_comment.updated",
    "channel.unread_changed",
    "decision_draft.resolved",
    "heartbeat_run.completed",
    "pulse_session.created",
    "pulse_session.updated",
    "pulse_briefing.updated",
    "coding_event",
    "runs.created",
    "runs.updated",
    "connector_sync.progress",
    "desk_proposals.updated",
    "autonomy.undo_countdown",
    "autonomy.action_shipped",
    "autonomy.rule_proposed",
    "autonomy.rule_deprecation_suggested",
  ].includes(type);
}

function isNewWsEvent(parsed: unknown): parsed is WsEvent {
  if (!parsed || typeof parsed !== "object") return false;
  const t = (parsed as { type?: unknown }).type;
  return t === "entity_change" || t === "stream" || t === "interactive";
}

function dispatchEntityChange(event: EntityChangeEvent) {
  window.dispatchEvent(new CustomEvent("ws:entity_change", { detail: event }));
  window.dispatchEvent(
    new CustomEvent(`ws:entity_change:${event.entity_type}`, { detail: event })
  );
}

function dispatchStream(event: StreamEvent) {
  window.dispatchEvent(new CustomEvent("ws:stream", { detail: event }));
}

function dispatchInteractive(event: InteractiveEvent) {
  window.dispatchEvent(new CustomEvent("ws:interactive", { detail: event }));
  window.dispatchEvent(
    new CustomEvent(`ws:interactive:${event.kind}`, { detail: event })
  );
}

// Existing per-event-type dispatch path. Preserves the pre-Task-3.3 behavior
// of forwarding every legacy frame to the caller's onMessage and re-emitting
// builder/run/work_item events as window CustomEvents for independent hooks.
// Section 11.3 of the architecture simplification plan deletes this path
// once all emit sites and consumers have migrated to the new shapes.
function handleLegacyEvent(
  msg: WsServerMessage,
  onMessage: (msg: WsServerMessage) => void
) {
  onMessage(msg);
  if (
    msg.type === "builder.activity" ||
    msg.type === "builder.status" ||
    msg.type === "run.activity" ||
    msg.type === "run.status" ||
    msg.type === "work_item.updated" ||
    msg.type === "work_item_activity.created" ||
    msg.type === "coding_event" ||
    msg.type === "runs.created" ||
    msg.type === "runs.updated"
  ) {
    window.dispatchEvent(new CustomEvent(msg.type, { detail: msg }));
  }
}

// Routes the new consolidated WS event shapes to their per-type handlers.
// Stub-or-passthrough during the cutover: emits CustomEvents on `window` so
// independent hooks/reducers can subscribe without coupling. Feature-rich
// reducers land in Section 5+ of the architecture simplification plan.
function handleNewWsEvent(event: WsEvent) {
  switch (event.type) {
    case "entity_change":
      dispatchEntityChange(event);
      return;
    case "stream":
      dispatchStream(event);
      return;
    case "interactive":
      dispatchInteractive(event);
      return;
  }
}

function parseGatewayServerMessage(parsed: unknown): WsServerMessage | null {
  if (!parsed || typeof parsed !== "object") {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  const directType = typeof record.type === "string" ? record.type : null;
  if (directType && isWsServerMessageType(directType)) {
    return record as WsServerMessage;
  }

  if (record.type === "event" && typeof record.event === "string") {
    if (
      isWsServerMessageType(record.event) &&
      record.payload &&
      typeof record.payload === "object"
    ) {
      return {
        type: record.event,
        ...(record.payload as Record<string, unknown>),
      } as WsServerMessage;
    }
  }

  if (record.event && typeof record.event === "object") {
    return parseGatewayServerMessage(record.event);
  }

  return null;
}

interface UseWebSocketOptions {
  onMessage: (msg: WsServerMessage) => void;
  onReconnect?: (send: (msg: WsClientMessage) => void) => void;
}

export function useWebSocket(
  onMessageOrOptions:
    | ((msg: WsServerMessage) => void)
    | UseWebSocketOptions
) {
  const options: UseWebSocketOptions =
    typeof onMessageOrOptions === "function"
      ? { onMessage: onMessageOrOptions }
      : onMessageOrOptions;

  const wsRef = useRef<WebSocket | null>(null);
  const backendCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const gatewayCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const backendReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const gatewayReconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pendingMessagesRef = useRef<WsClientMessage[]>([]);
  const onMessageRef = useRef(options.onMessage);
  const onReconnectRef = useRef(options.onReconnect);
  const [gatewayConnected, setGatewayConnected] = useState(false);
  const gatewayWsRef = useRef<WebSocket | null>(null);
  const backendAttemptRef = useRef(0);
  const gatewayAttemptRef = useRef(0);
  const backendCancelledRef = useRef(false);
  const gatewayCancelledRef = useRef(false);
  const gatewayAuthenticatedRef = useRef(false);
  // True once the gateway has authenticated at least once, so a later
  // authentication is a reconnect (first-connect info retries are not).
  const gatewayEverAuthenticatedRef = useRef(false);

  onMessageRef.current = options.onMessage;
  onReconnectRef.current = options.onReconnect;

  // Stable send that works across reconnects
  const send = useCallback((msg: WsClientMessage) => {
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(msg));
      return;
    }
    pendingMessagesRef.current.push(msg);
  }, []);

  // --- Backend WebSocket with reconnect ---
  useEffect(() => {
    if (backendCloseTimerRef.current !== null) {
      clearTimeout(backendCloseTimerRef.current);
      backendCloseTimerRef.current = null;
    }

    backendCancelledRef.current = false;

    if (
      wsRef.current &&
      wsRef.current.readyState !== WebSocket.CLOSED
    ) {
      return () => {
        backendCancelledRef.current = true;
        backendCloseTimerRef.current = setTimeout(() => {
          wsRef.current?.close();
          wsRef.current = null;
        }, 100);
      };
    }

    function connectBackend() {
      if (backendCancelledRef.current) return;

      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        const isReconnect = backendAttemptRef.current > 0;
        backendAttemptRef.current = 0;

        for (const pending of pendingMessagesRef.current) {
          ws.send(JSON.stringify(pending));
        }
        pendingMessagesRef.current = [];

        if (isReconnect) {
          onReconnectRef.current?.(send);
          dispatchReconnected();
        }
      };

      ws.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data) as unknown;
          // New consolidated WS event shapes (entity_change/stream/interactive)
          // route through a dedicated dispatcher; legacy per-type messages keep
          // flowing through onMessage during the cutover window.
          if (isNewWsEvent(parsed)) {
            handleNewWsEvent(parsed);
            return;
          }
          const msg = parsed as WsServerMessage;
          handleLegacyEvent(msg, onMessageRef.current);
        } catch {
          // Ignore malformed frames (e.g. proxy error pages, partial writes)
        }
      };

      ws.onclose = () => {
        if (wsRef.current === ws) {
          wsRef.current = null;
        }
        if (!backendCancelledRef.current) {
          const delay = reconnectDelay(backendAttemptRef.current);
          backendAttemptRef.current++;
          backendReconnectTimerRef.current = setTimeout(connectBackend, delay);
        }
      };
    }

    connectBackend();

    return () => {
      backendCancelledRef.current = true;
      if (backendReconnectTimerRef.current !== null) {
        clearTimeout(backendReconnectTimerRef.current);
        backendReconnectTimerRef.current = null;
      }
      backendCloseTimerRef.current = setTimeout(() => {
        wsRef.current?.close();
        wsRef.current = null;
      }, 100);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Gateway WebSocket with reconnect ---
  useEffect(() => {
    if (gatewayCloseTimerRef.current !== null) {
      clearTimeout(gatewayCloseTimerRef.current);
      gatewayCloseTimerRef.current = null;
    }

    gatewayCancelledRef.current = false;

    if (
      gatewayWsRef.current &&
      gatewayWsRef.current.readyState !== WebSocket.CLOSED
    ) {
      setGatewayConnected(gatewayAuthenticatedRef.current);

      return () => {
        gatewayCancelledRef.current = true;
        gatewayCloseTimerRef.current = setTimeout(() => {
          gatewayWsRef.current?.close();
          gatewayWsRef.current = null;
          gatewayAuthenticatedRef.current = false;
          setGatewayConnected(false);
        }, 100);
      };
    }

    function connectGateway() {
      if (gatewayCancelledRef.current) return;

      void fetchGatewayInfo().then((info) => {
        if (gatewayCancelledRef.current) return;

        // Direct mode: no gateway WS needed — all events flow through backend WS
        if (info?.mode === "direct") {
          gatewayAuthenticatedRef.current = true;
          setGatewayConnected(true);
          return;
        }

        if (!info) {
          const delay = reconnectDelay(gatewayAttemptRef.current);
          gatewayAttemptRef.current++;
          gatewayReconnectTimerRef.current = setTimeout(connectGateway, delay);
          return;
        }

        let authenticated = false;
        const connectRequestId = `connect-${Date.now()}`;
        const wsUrl = buildGatewayWsUrl(info.gatewayUrl);
        if (!wsUrl) {
          // Malformed gateway URL — treat like missing info and retry with backoff.
          const delay = reconnectDelay(gatewayAttemptRef.current);
          gatewayAttemptRef.current++;
          gatewayReconnectTimerRef.current = setTimeout(connectGateway, delay);
          return;
        }
        const gatewayWs = new WebSocket(wsUrl);
        gatewayWsRef.current = gatewayWs;

        gatewayWs.onopen = () => {
          if (gatewayCancelledRef.current) {
            gatewayWs.close();
            return;
          }

          gatewayWs.send(
            JSON.stringify({
              type: "req",
              id: connectRequestId,
              method: "connect",
              params: {
                auth: {
                  token: info.gatewayToken,
                },
              },
            })
          );
        };

        gatewayWs.onmessage = (event) => {
          if (gatewayCancelledRef.current) return;
          try {
            const parsed = JSON.parse(event.data) as unknown;

            if (!authenticated) {
              const connectResponse = parsed as GatewayConnectResponse;
              if (
                connectResponse.type === "res" &&
                connectResponse.id === connectRequestId
              ) {
                if (connectResponse.error) {
                  gatewayWs.close();
                  return;
                }

                authenticated = true;
                gatewayAttemptRef.current = 0;
                gatewayAuthenticatedRef.current = true;
                setGatewayConnected(true);
                if (gatewayEverAuthenticatedRef.current) dispatchReconnected();
                gatewayEverAuthenticatedRef.current = true;
                return;
              }
            }

            // Gateway also dual-handles: new consolidated shapes route
            // through handleNewWsEvent, legacy frames through onMessage.
            if (isNewWsEvent(parsed)) {
              handleNewWsEvent(parsed);
              return;
            }
            const message = parseGatewayServerMessage(parsed);
            if (message) {
              handleLegacyEvent(message, onMessageRef.current);
            }
          } catch {
            // Ignore unparseable messages from gateway
          }
        };

        gatewayWs.onclose = () => {
          if (gatewayWsRef.current === gatewayWs) {
            gatewayWsRef.current = null;
          }

          gatewayAuthenticatedRef.current = false;

          if (!gatewayCancelledRef.current) {
            setGatewayConnected(false);
            const delay = reconnectDelay(gatewayAttemptRef.current);
            gatewayAttemptRef.current++;
            gatewayReconnectTimerRef.current = setTimeout(
              connectGateway,
              delay
            );
          }
        };
      });
    }

    connectGateway();

    return () => {
      gatewayCancelledRef.current = true;
      if (gatewayReconnectTimerRef.current !== null) {
        clearTimeout(gatewayReconnectTimerRef.current);
        gatewayReconnectTimerRef.current = null;
      }
      gatewayCloseTimerRef.current = setTimeout(() => {
        gatewayWsRef.current?.close();
        gatewayWsRef.current = null;
        gatewayAuthenticatedRef.current = false;
        // Don't call setGatewayConnected here — the component is unmounting,
        // so a state update is unnecessary and can fire after the environment
        // is torn down in tests (causing "window is not defined").
      }, 100);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { send, gatewayConnected };
}
